import { useState } from 'react';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { parseUploadedWorkbook, applyStrandOverride } from '../utils/parseStudentImport';
import { STRANDS_BY_GRADE } from '../../../shared/constant/strands';

function StudentImportModal({ onClose, onImport, existingLrns = [] }) {
  const [rows, setRows] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [strand, setStrand] = useState('');
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  const validRows = rows.filter((r) => r.valid);
  const invalidRows = rows.filter((r) => !r.valid);
  const strandOptions = meta ? STRANDS_BY_GRADE[meta.gradeLevel] ?? [] : [];

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setImportError('');
    setRows([]);
    setRawRows([]);
    setMeta(null);
    setStrand('');

    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseUploadedWorkbook(buffer, existingLrns);
      if (parsed.rows.length === 0) {
        setParseError('No rows found in the uploaded file.');
        return;
      }
      setRows(parsed.rows);
      setRawRows(parsed.rawRows);
      setMeta(parsed.meta);
      if (parsed.meta?.requiresStrand) setStrand(parsed.meta.detectedStrand || '');
    } catch (err) {
      setParseError(err.message || 'Failed to read the file. Make sure it is a valid .xlsx or .xls file.');
    }
  };

  const handleStrandChange = (e) => {
    const value = e.target.value;
    setStrand(value);
    const result = applyStrandOverride(rawRows, value, existingLrns);
    setRawRows(result.rawRows);
    setRows(result.rows);
  };

  const handleImport = async () => {
    setImportError('');
    setImporting(true);
    try {
      await onImport(validRows.map((r) => r.data));
      onClose();
    } catch (err) {
      setImportError(err.message || 'Failed to import students');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 bg-navy-light">
          <h2 className="text-lg font-semibold text-white">Import Students</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-200 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {importError && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{importError}</p>
          )}
          {parseError && (
            <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">{parseError}</p>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Upload filled-in template (.xlsx) or a DepEd SF1 export (.xls)
            </label>
            <label className="flex items-center gap-2 w-fit rounded-md border border-gray-300 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
              <ArrowUpTrayIcon className="h-4 w-4" />
              {fileName || 'Choose file'}
              <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="hidden" />
            </label>
          </div>

          {meta && (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 space-y-2">
              <p className="text-xs text-gray-600">
                Detected from DepEd SF1: <span className="font-medium">{meta.gradeLevel || '—'}</span>
                {meta.section && <> • Section <span className="font-medium">{meta.section}</span></>}
                {meta.schoolYear && <> • SY <span className="font-medium">{meta.schoolYear}</span></>}
              </p>
              {meta.requiresStrand && (
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-700">Strand</label>
                  <select
                    value={strand}
                    onChange={handleStrandChange}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  >
                    <option value="">Select</option>
                    {strandOptions.map(({ code, label }) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {!strand && (
                    <span className="text-xs text-danger">
                      Could not auto-detect the strand — please confirm it.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="flex gap-4 text-sm">
                <span className="text-success font-medium">{validRows.length} ready to import</span>
                {invalidRows.length > 0 && (
                  <span className="text-danger font-medium">{invalidRows.length} skipped (errors)</span>
                )}
              </div>

              <div className="overflow-x-auto max-h-72 overflow-y-auto border border-gray-200 rounded-md">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase">
                      <th className="py-2 pl-3 pr-4 font-semibold whitespace-nowrap">Row</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Name</th>
                      <th className="py-2 pr-4 font-semibold whitespace-nowrap">Grade</th>
                      <th className="py-2 pr-3 font-semibold whitespace-nowrap">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((row) => (
                      <tr key={row.rowNumber} className={row.valid ? '' : 'bg-danger/5'}>
                        <td className="py-2 pl-3 pr-4 text-gray-500 whitespace-nowrap">{row.rowNumber}</td>
                        <td className="py-2 pr-4 text-gray-800 whitespace-nowrap">
                          {row.data.firstname} {row.data.lastname}
                        </td>
                        <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{row.data.grade_level || '—'}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {row.valid ? (
                            <span className="text-success text-xs font-medium">Ready</span>
                          ) : (
                            <span className="text-danger text-xs" title={row.errors.join('; ')}>
                              {row.errors.join('; ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-28 py-2 text-center text-sm font-medium text-gray-600 border border-gray-300 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={validRows.length === 0 || importing}
            onClick={handleImport}
            className="w-40 py-2 rounded-md bg-navy-light text-white text-sm font-medium text-center hover:bg-navy disabled:opacity-50"
          >
            {importing ? 'Importing…' : `Import ${validRows.length || ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentImportModal;
